import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  MyCosmeticsSchema,
  NO_COSMETICS,
  type AppliedCosmetics,
} from "@legacy-manager/shared";
import type {
  CalendarRow,
  ClubView,
  FraseDaSessao,
  MatchSummary,
  LineupInput,
  MarketFilters,
  OrdemDoMercado,
  MarketPlayerRow,
  TacticInput,
  YouthAction,
  LivreRow,
} from "../game/types";
import { useSessao } from "../online/sessao";
import { Crest, FormChips, MoraleFace, Stars } from "../ui/atoms";
import { BotaoDoClube } from "./FichaDoClube";
import { EscudoDoClube } from "../ui/brasoes/Escudo";
import { EscudoCosmetico, UniformeCosmetico, estiloDeEstadio } from "../ui/cosmeticos";
import type { CrestParams } from "@legacy-manager/shared";
import {
  dataDaRodada,
  dinheiro,
  numero,
  rotuloDeFase,
  rotuloDoMotor,
  textoDaSessao,
  useT,
  type Traduzir,
} from "../i18n";
import { Icone, IconSeta } from "../ui/icons";
import { BarraDeNavegacao, type DestinoDeNavegacao } from "../ui/BarraDeNavegacao";
import { useTelaEstreita } from "../ui/tela";
import { FinancePanel, SquadPanel } from "./panels";
import { Jornal } from "./Jornal";
import { FilaDaRodada } from "./FilaDaRodada";
import { PropostasRecebidas } from "./PropostasRecebidas";
import { SinoDeNotificacoes } from "./SinoDeNotificacoes";
import { FichaDoJogador, PlayerSheet, type DadosDaFicha } from "./PlayerSheet";
import { AjudaDeAtalhos } from "./AjudaDeAtalhos";
import { ClubPanel } from "./ClubPanel";
import { CareerPanel } from "./CareerPanel";
import { Calendario, CompetitionsPanel, DialogoDoConfronto } from "./CompetitionsPanel";
import { ColunaDoAdm } from "./ColunaDoAdm";
import { RelatoriosPanel } from "./RelatoriosPanel";
import { MarketPanel } from "./MarketPanel";
import { Conta } from "./Conta";
import { Ligas } from "./Ligas";
import { Loja } from "./Loja";
import { Opcoes } from "./Opcoes";
import { Ranqueado } from "./Ranqueado";
import { TacticsPanel } from "./TacticsPanel";
import { TimeBPanel, type AcoesDoTimeB } from "./TimeBPanel";
import { DialogoDeSaida, type Saida } from "../ui/DialogoDeSaida";
import { abrirLinhaMarcada, focarBusca, moverNaLista } from "../ui/atalhos";
import { useAtalhosDeTeclado } from "../ui/useAtalhos";

/*
 * As seções do hub, na forma da tela ADM do Clube (mapeamento de 25/09/2026).
 *
 * "elenco" é a própria ADM: a tela para onde se volta. Finanças e Carreira
 * viraram abas de Clube / Finanças; Calendário saiu de dentro de Competições;
 * Relatórios é nova; "base" aparece como Time B.
 */
type Section =
  | "elenco"
  | "tatica"
  | "mercado"
  | "competicoes"
  | "clube"
  | "calendario"
  | "base"
  | "relatorios"
  | "noticias"
  | "opcoes"
  /*
   * Estas quatro nunca chegam a ser a seção aberta.
   *
   * Elas são telas inteiras do aplicativo, com o próprio voltar, e o destino
   * sai do hub em vez de trocar a seção. Estão no tipo porque a barra de
   * navegação precisa de uma chave para cada item que desenha.
   */
  | "ligas"
  | "ranqueado"
  | "loja"
  | "conta";

/** As abas de Clube / Finanças. */
type AbaDoClube = "clube" | "financas" | "carreira";

export interface HubActions {
  /** Assistir à partida do clube em vez de pular direto para o resultado. */
  onWatch: () => void;
  /** Passar a vez para outro treinador da mesma partida, no hotseat. */
  onSwitchSeat: (index: number) => void;
  /** Salvar o banco de dados desta carreira num arquivo. */
  onExportPack: () => void;
  /** Marca as notificações do clube como lidas, ao abrir o sino. */
  onLerNotificacoes: () => void;
  /** Responde a coletiva aberta. Nulo é "sem comentários". */
  onResponderColetiva: (coletivaId: string, resposta: 0 | 1 | 2 | null) => void;
  /** Salvar a carreira agora, com o salvamento automático desligado. */
  onSaveNow: () => void;
  onConfirmLineup: (lineup: LineupInput, tactic: TacticInput) => void;
  onAutoLineup: (tactic: TacticInput) => void;
  onSearch: (filters: MarketFilters, pagina: number, ordem: OrdemDoMercado | null) => void;
  onOpenPlayer: (playerId: string) => void;
  /** Abre outro clube: o time provável e o elenco inteiro. */
  onOpenClub: (clubId: string) => void;
  onRespondOffer: (offerId: string, action: "aceitar" | "recusar") => void;
  /** Promover, segurar mais um ano ou dispensar um garoto da base, pela fila. */
  onYouthAction: (prospectId: string, action: YouthAction) => void;
  /** As ações do Time B e dos Livres, que valem na hora. */
  timeB: AcoesDoTimeB & {
    livres: () => Promise<LivreRow[]>;
  };
  onCancelDecision: (index: number) => void;
  onClubAction: (input: {
    ticketPrice?: number;
    loan?: number;
    repay?: number;
    build?: { facility: string; targetLevel: number };
  }) => void;
  onExit: () => void;
  /** Sair da carreira pelo diálogo: gravar e sair, ou sair sem gravar. */
  saida: Saida;
}

/**
 * Hub do clube.
 *
 * Segue a estrutura dos conceitos de arte: barra lateral com a navegação,
 * cabeçalho com a identidade e os números do clube, o conteúdo ocupando a maior
 * parte da tela e uma coluna de painéis à direita. O botão de avançar a rodada
 * fica sempre visível na barra inferior, junto com quantas decisões esperam.
 */
/**
 * Os cosméticos que o servidor liberou para esta conta.
 *
 * O modo solo não tem liga para carregar essa informação junto, então ele
 * pergunta. E pergunta **ao servidor**, que é quem confere o direito: o
 * cabeçalho desenha o que veio em `applied`, nunca o que o cliente acharia.
 *
 * Quem joga sem conta, ou sem internet, não perde nada: a leitura nem acontece
 * e o clube fica com o escudo de sempre. Jogar sozinho continua não precisando
 * de conta, que é a promessa da tela de login.
 */
function useCosmeticosDaConta(): AppliedCosmetics {
  const sessao = useSessao();
  const [aplicados, definirAplicados] = useState<AppliedCosmetics>(NO_COSMETICS);
  // Quantos a conta tem, que é o sinal que a sessão atualiza quando uma compra
  // é confirmada pelo socket. Serve de gatilho sem virar leitura em laço.
  const quantos = sessao.conta?.direitos.cosmetics.length ?? 0;

  useEffect(() => {
    if (quantos === 0) {
      definirAplicados(NO_COSMETICS);
      return;
    }
    let vivo = true;
    void sessao.cliente
      .pedir({ metodo: "GET", caminho: "/v1/me/cosmetics" }, MyCosmeticsSchema)
      .then((meus) => {
        if (vivo) definirAplicados(meus.applied);
      })
      .catch(() => {
        // Falha aqui é enfeite que não apareceu, não erro de jogo.
        if (vivo) definirAplicados(NO_COSMETICS);
      });
    return () => {
      vivo = false;
    };
  }, [sessao, quantos]);

  return aplicados;
}

export function ClubHub({
  ficha,
  view,
  busy,
  note,
  market,
  actions,
  online,
  ultimoJogo,
}: {
  view: ClubView;
  /** O resumo da última partida desta sessão, para o pós-jogo dos relatórios. */
  ultimoJogo: MatchSummary | null;
  busy: boolean;
  /** O recado da rodada, como chave e valores. A frase é escrita aqui. */
  note: FraseDaSessao | null;
  market: { results: MarketPlayerRow[]; total: number; pagina: number; paginas: number };
  /** O jogador aberto e o que se pode fazer com ele, ou `null`. */
  ficha: DadosDaFicha | null;
  actions: HubActions;
  /**
   * Os destinos que só existem com conta.
   *
   * `null` no modo 1 Jogador, e então eles nem aparecem na navegação: ligas,
   * ranqueado, loja e conta não têm o que fazer numa carreira que roda inteira
   * no aparelho.
   */
  online: {
    /**
     * Entrar numa liga online, que é a única coisa daqui que sai do hub.
     *
     * Ligas, ranqueado, loja e conta viraram seção e rodam dentro do jogo, como
     * elenco e tática. Jogar uma liga é outra coisa: é outro mundo, outro
     * calendário e outro clube, então ali a tela troca de verdade.
     */
    aoAbrirLiga: (leagueId: string) => void;
  } | null;
}) {
  const t = useT();
  const [section, setSection] = useState<Section>("elenco");
  const [abaDoMercado, setAbaDoMercado] = useState<"procurar" | "livres">("procurar");
  const [saindo, setSaindo] = useState(false);
  const [abaDoClube, setAbaDoClube] = useState<AbaDoClube>("clube");
  const [confronto, setConfronto] = useState<CalendarRow | null>(null);
  const [ajuda, setAjuda] = useState(false);
  const estreita = useTelaEstreita();
  const [dadosAbertos, definirDadosAbertos] = useState(dadosGuardados);
  const cosmeticos = useCosmeticosDaConta();
  const { club } = view;

  /*
   * Atalhos de teclado (FRONTEND_GUIDE 6).
   *
   * O hub é quem sabe executar cada ação, então é aqui que o gancho mora. Os
   * diálogos não montam ouvinte próprio: eles se registram e este mesmo ouvinte
   * os atende, que é como a regra de "diálogo aberto silencia o hub" se sustenta.
   */
  useAtalhosDeTeclado((acao, tecla) => {
    switch (acao) {
      case "assento": {
        // Hotseat: 1 a 9 passam a vez, e só quando há outro treinador na mesa.
        const indice = Number(tecla) - 1;
        const assento = view.seats[indice];
        if (view.seats.length > 1 && assento && !assento.active && !busy) {
          actions.onSwitchSeat(indice);
        }
        return;
      }
      /*
       * A tecla faz o mesmo que o botão, que é abrir a partida.
       *
       * Eram duas teclas para duas ações que viraram uma. Deixar a de jogar
       * pulando a partida enquanto o botão a abre seria um atalho que faz coisa
       * diferente do botão que ele imita, e ninguém descobre isso sem apertar
       * sem querer.
       */
      case "jogar":
        if (!busy) actions.onWatch();
        return;
      case "elenco":
        setSection("elenco");
        return;
      case "tatica":
        setSection("tatica");
        return;
      case "mercado":
        setSection("mercado");
        return;
      case "clube":
        setAbaDoClube("clube");
        setSection("clube");
        return;
      case "competicoes":
        setSection("competicoes");
        return;
      case "noticias":
        setSection("noticias");
        return;
      case "financas":
        setAbaDoClube("financas");
        setSection("clube");
        return;
      case "calendario":
        setSection("calendario");
        return;
      case "timeB":
        setSection("base");
        return;
      case "relatorios":
        setSection("relatorios");
        return;
      case "busca":
        setSection("mercado");
        focarBusca();
        return;
      case "ajuda":
        setAjuda(true);
        return;
      case "voltar":
        // Sem painel aberto, Esc volta para a tela inicial do hub. Na tela
        // inicial, primeiro desmarca o jogador da coluna; sem jogador marcado,
        // abre a saída da carreira.
        if (section !== "elenco") setSection("elenco");
        else if (ficha && colunaDoAdm) ficha.onClose();
        else setSaindo(true);
        return;
      case "listaAcima":
        moverNaLista(-1);
        return;
      case "listaAbaixo":
        moverNaLista(1);
        return;
      case "listaAbrir":
        abrirLinhaMarcada();
        return;
    }
  });

  /*
   * Os destinos, na ordem em que a coluna da esquerda mostra.
   *
   * `principal` é quem fica na barra do celular: as quatro telas de toda
   * rodada. O resto vai para a folha que sobe, inclusive loja, ranqueado,
   * ligas e conta, que são telas inteiras e saem do hub em vez de trocar a
   * seção. Elas entram aqui porque é aqui que se procura por elas: antes
   * moravam na tela de abertura, que ninguém mais vê depois de entrar.
   */
  /*
   * O menu da tela ADM do Clube: o elenco, que é a tela inicial, e os oito
   * itens do mapeamento, com Notícias à parte. O E e o Esc também voltam ao
   * elenco de qualquer seção.
   */
  const nav: Array<DestinoDeNavegacao<Section>> = [
    { chave: "elenco", rotulo: "hub.nav.elenco", icone: <Icone nome="Elenco" />, principal: true },
    { chave: "clube", rotulo: "hub.nav.clubeFinancas", icone: <Icone nome="Clube" /> },
    { chave: "tatica", rotulo: "hub.nav.tatica", icone: <Icone nome="Tatica" />, principal: true },
    { chave: "calendario", rotulo: "hub.nav.calendario", icone: <Icone nome="Carreira" /> },
    { chave: "competicoes", rotulo: "hub.nav.competicoes", icone: <Icone nome="Competicoes" /> },
    {
      chave: "mercado",
      rotulo: "hub.nav.mercado",
      icone: <Icone nome="Mercado" />,
      principal: true,
    },
    { chave: "base", rotulo: "hub.nav.timeB", icone: <Icone nome="Base" />, principal: true },
    { chave: "relatorios", rotulo: "hub.nav.relatorios", icone: <Icone nome="Caixa" /> },
    /* O crachá acende quando há coletiva esperando resposta. */
    { chave: "noticias", rotulo: "hub.nav.noticias", icone: <Icone nome="Noticias" />, aviso: view.coletiva ? 1 : 0 },
    /*
     * Sem `aoEscolher`: estas quatro trocam a seção como qualquer outra.
     *
     * Antes elas saíam do hub e viravam página inteira, com o próprio voltar.
     * A pessoa perdia o cabeçalho do clube, a coluna da direita e a barra de
     * ação, e tinha que se orientar de novo para achar o caminho de volta.
     */
    ...(online
      ? ([
          { chave: "ligas", rotulo: "ligas.titulo", icone: <Icone nome="Ligas" /> },
          {
            chave: "ranqueado",
            rotulo: "ranqueado.titulo",
            icone: <Icone nome="Ranqueado" />,
          },
          { chave: "loja", rotulo: "loja.titulo", icone: <Icone nome="Loja" /> },
          { chave: "conta", rotulo: "conta.titulo", icone: <Icone nome="Conta" /> },
        ] as Array<DestinoDeNavegacao<Section>>)
      : []),
    /* Por último, e longe do botão de avançar a rodada: quem abre opções não
       está jogando, está arrumando alguma coisa antes de voltar a jogar. */
    { chave: "opcoes", rotulo: "hub.nav.configuracoes", icone: <Icone nome="Opcoes" /> },
  ];

  /*
   * Campanha em um campo só: "12-4-6" se lê de relance e ocupa o lugar de um
   * fato, contra três que os números separados pediriam numa faixa de onze.
   */
  const record = view.record;
  const campanha = `${record.wins}-${record.draws}-${record.losses}`;

  const moral =
    club.moraleAverage >= 80
      ? t("hub.moralExcelente")
      : club.moraleAverage >= 62
        ? t("hub.moralBoa")
        : club.moraleAverage >= 45
          ? t("hub.moralRegular")
          : t("hub.moralRuim");

  /*
   * A ficha vira coluna no elenco e no mercado, em tela larga.
   *
   * São as duas telas que são uma lista de jogadores: clicar numa linha troca a
   * ficha ao lado, sem diálogo por cima. Nas outras seções não há lista para
   * ficar ao lado, e no celular não há largura: ela volta a abrir por cima.
   */
  /*
   * No elenco, a coluna da direita é uma coisa ou outra.
   *
   * Sem jogador marcado, ela é o resumo do clube (próximo jogo, campanha,
   * finanças, rivalidades). Clicar num jogador troca o resumo pela ficha dele;
   * clicar de novo no mesmo, ou o Esc, desmarca e o resumo volta. Vale em
   * qualquer largura com coluna; no celular a ficha continua abrindo por cima.
   */
  const colunaDoAdm = section === "elenco" && !estreita;
  const fichaNaColuna = (section === "elenco" || section === "mercado") && !estreita;

  /*
   * A coluna abre com o primeiro do elenco, e não vazia.
   *
   * Um vão de 360 pixels esperando o primeiro clique parece defeito, e pôr ali
   * uma frase dizendo para clicar seria escrever na tela o que a tela já faz. O
   * primeiro goleiro é escolha sem intenção nenhuma: é só o topo da lista.
   */
  const primeiroDoElenco =
    section === "elenco"
      ? view.squad[0]?.id
      : section === "mercado"
        ? market.results.find((jogador) => !view.squad.some((meu) => meu.id === jogador.id))?.id
        : undefined;
  /*
   * O pedido é assíncrono, e `actions` é objeto novo a cada render.
   *
   * Sem a trava, cada render entre o pedido e a resposta pediria de novo o
   * mesmo jogador, e são vários: a resposta vem do worker. A trava guarda o id
   * já pedido, o que também impede reabrir sozinho o jogador que a pessoa
   * fechou.
   */
  const jaPedido = useRef<string | null>(null);
  useEffect(() => {
    // No elenco a coluna começa no resumo do clube, e não num jogador; nos
    // Livres, ela espera o clique.
    if (section !== "mercado" || abaDoMercado !== "procurar") return;
    if (!fichaNaColuna || ficha || !primeiroDoElenco) return;
    if (jaPedido.current === primeiroDoElenco) return;
    jaPedido.current = primeiroDoElenco;
    actions.onOpenPlayer(primeiroDoElenco);
  }, [section, abaDoMercado, fichaNaColuna, ficha, primeiroDoElenco, actions]);

  /*
   * A ficha da coluna morre com a coluna.
   *
   * Sem isto, sair do elenco com um jogador aberto abria a ficha como diálogo
   * por cima da tela seguinte: a coluna sumia, `ficha` continuava preenchida, e
   * o único lugar que sobrava para ela era o modal. Trocar de seção passava a
   * abrir uma janela que ninguém pediu.
   *
   * A regra é a coluna e não a seção, porque estreitar a janela abaixo de 820
   * tira a coluna do mesmo jeito e daria o mesmo susto.
   *
   * Isto não alcança a ficha aberta no mercado, na base ou na artilharia: lá
   * `fichaNaColuna` nunca foi verdadeiro, então nada se fecha sozinho e o
   * diálogo continua sendo o que era.
   */
  const tinhaColuna = useRef(fichaNaColuna);
  /*
   * No mercado, a ficha é de alguém da lista que está na tela.
   *
   * A busca responde depois, pelo worker. Ao voltar ao mercado, a coluna abria
   * o primeiro da busca anterior enquanto a nova não chegava; quando ela
   * chegava diferente, ou vazia, a ficha ficava lá mostrando um jogador que a
   * lista não tinha. Fora da lista, a ficha fecha, e a coluna reabre no
   * primeiro da lista nova, se houver alguém nela.
   */
  useEffect(() => {
    if (section !== "mercado" || abaDoMercado !== "procurar" || !fichaNaColuna || !ficha) return;
    if (market.results.some((jogador) => jogador.id === ficha.player.id)) return;
    ficha.onClose();
    jaPedido.current = null;
  }, [section, abaDoMercado, fichaNaColuna, ficha, market.results]);

  /* Trocar a aba do mercado fecha a ficha da outra lista. */
  const abaAnterior = useRef(abaDoMercado);
  useEffect(() => {
    if (abaAnterior.current === abaDoMercado) return;
    abaAnterior.current = abaDoMercado;
    if (ficha) ficha.onClose();
    jaPedido.current = null;
  }, [abaDoMercado, ficha]);

  /*
   * Trocar de seção com coluna também fecha a ficha.
   *
   * Elenco e mercado têm coluna os dois, então passar de um para o outro não
   * derruba a coluna, e sem isto o jogador do mercado aparecia ao lado do
   * elenco. Cada seção reabre no primeiro da sua própria lista.
   */
  const secaoDaColuna = useRef(section);
  useEffect(() => {
    const trocouDeSecao = secaoDaColuna.current !== section;
    // Trocar de seção fecha qualquer ficha, e não só a da coluna: a aberta como
    // diálogo em outra seção ia parar na coluna do mercado ao entrar nele.
    if (ficha && (trocouDeSecao || (tinhaColuna.current && !fichaNaColuna))) {
      ficha.onClose();
      // Voltar reabre no primeiro, como na primeira vez.
      jaPedido.current = null;
    }
    if (trocouDeSecao) jaPedido.current = null;
    tinhaColuna.current = fichaNaColuna;
    secaoDaColuna.current = section;
  }, [fichaNaColuna, ficha, section]);

  const advanceLabel = view.seasonOver
    ? t("hub.proximaTemporada")
    : view.nextMatch
      ? rotuloDoJogo(t, view.nextMatch)
      : t("hub.avancarRodada");

  return (
    <div className="shell" data-round={view.round} data-season={view.season}>
      <BarraDeNavegacao
        destinos={nav}
        atual={section}
        aoTrocar={setSection}
        aria={t("hub.navAria")}
        aoAbrirAtalhos={() => setAjuda(true)}
      />

      <div className="main" data-secao={section}>
        {/*
          O cabeçalho carrega duas coisas: quem é o clube e como ele está.

          Antes só a primeira ficava aqui, e a segunda morava numa coluna à
          direita que desaparecia abaixo de 1180px de tela. Ali ela virava um
          botão que trocava a página inteira, e quem queria saber contra quem
          era o próximo jogo saía do elenco, olhava e voltava.
        */}
        <header className="club-header" style={estiloDeEstadio(cosmeticos.stadium)}>
          <div className="club-header-identidade">
          {escudoDoClube(club, cosmeticos.crest, 54)}
          {cosmeticos.kit && <UniformeCosmetico params={cosmeticos.kit} size={44} />}
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 20, letterSpacing: "-0.01em" }}>{club.name}</h1>
            <div className="row" style={{ gap: 10, marginTop: 4 }}>
              <span className="muted" style={{ fontSize: 12 }}>
                {club.countryName}
              </span>
              <Stars level={club.reputation} />
              <span className="label">{rotuloDoMotor(club.reputationKey)}</span>
              {/*
                A seta só existe no celular, e é onde ela cabe.

                No computador os onze fatos ficam numa faixa de 83 pixels e não
                há o que recolher. No celular eles viram cinco linhas empilhadas
                e empurram o elenco para baixo da dobra, e nem toda rodada
                precisa deles: o que se olha a cada rodada está na barra de
                baixo, com o próximo jogo.
              */}
              {estreita && (
                <button
                  className="ghost cabecalho-seta"
                  aria-expanded={dadosAbertos}
                  aria-controls="dados-do-clube"
                  aria-label={t(dadosAbertos ? "hub.fecharDados" : "hub.abrirDados")}
                  onClick={() => {
                    definirDadosAbertos((aberto) => {
                      guardarDados(!aberto);
                      return !aberto;
                    });
                  }}
                >
                  <IconSeta />
                </button>
              )}
            </div>
          </div>

          {/*
            Tudo numa faixa só, e nada em cartão.

            Isto já foi três painéis empilhados ao lado da identidade, com um
            anel de pontos, uma tabela de finanças e barras de rivalidade: 335
            pixels de cabeçalho antes de o elenco começar. Nenhum dos três
            mostrava nada que não coubesse numa linha de rótulo e valor, e o de
            finanças repetia a seção Caixa inteira.
          */}
          {/*
            Quatro grupos, separados por vão e não por título.

            O que é do clube, o que você tem, como vai a campanha e o que a
            diretoria cobra. Em fileira única os onze fatos se liam como uma
            lista sem começo nem fim, e a moral do elenco ficava plantada entre
            o saldo de gols e os últimos jogos, cortando a campanha no meio.

            Sem rótulo de grupo: onze rótulos já são onze palavras na faixa, e
            mais quatro por cima diriam o que o vão já diz.
          */}
          <div className="header-facts" id="dados-do-clube" hidden={estreita && !dadosAbertos}>
            <div className="header-grupo">
              <Fact label={t("hub.fatos.treinador")} value={view.managerName} />
              <Fact label={t("hub.fatos.reputacaoTecnico")}>
                <span className="row" style={{ gap: 6 }}>
                  <Stars level={(view.managerReputation / 100) * 6} size={12} />
                  {t(rotuloDaReputacao(view.managerReputation))}
                </span>
              </Fact>
              <Fact
                label={t("hub.fatos.estadio")}
                value={t("hub.lugares", { capacidade: numero(club.stadiumCapacity) })}
              />
              {/*
                Ano e fundador no mesmo fato, e não em dois.
                
                São a mesma informação: quando e por quem. Separados, o cabeçalho
                ganharia um décimo segundo fato que fica vazio em 320 dos 321
                clubes, porque só o clube criado por alguém tem fundador.
              */}
              <Fact
                label={t("hub.fatos.fundacao")}
                value={
                  club.founder
                    ? `${String(club.founded)} · ${club.founder}`
                    : String(club.founded)
                }
              />
            </div>

            {!colunaDoAdm && (
            <div className="header-grupo">
              <Fact label={t("hub.fatos.caixa")} value={dinheiro(club.cash)} />
              <Fact label={t("hub.fatos.moral")}>
                <span className="row" style={{ gap: 5 }}>
                  <MoraleFace value={club.moraleAverage} size={15} />
                  {t("hub.moralValor", { rotulo: moral, valor: club.moraleAverage })}
                </span>
              </Fact>
            </div>
            )}

            {!colunaDoAdm && (
            <div className="header-grupo">
              <Fact
                label={t("hub.posicao")}
                value={
                  club.hasTable
                    ? t("hub.posicaoValor", { posicao: club.position })
                    : t("comum.vazio")
                }
              />
              <Fact label={t("hub.fatos.pontos")} value={String(record.points)} />
              <Fact label={t("hub.fatos.campanha")} value={campanha} />
              <Fact
                label={t("hub.saldo")}
                value={String(record.goalsFor - record.goalsAgainst)}
              />
              <Fact label={t("hub.ultimosJogos")}>
                <FormChips form={club.form} />
              </Fact>
            </div>
            )}

            <div className="header-grupo">
              <Fact
                label={t("hub.fatos.objetivo")}
                value={rotuloDoMotor(club.boardLabelKey)}
                quebra
              />
            </div>
          </div>

          {/*
            A data vai para o extremo direito e fica lá.

            `flex: 1` nos fatos já empurrava, mas só enquanto eles coubessem
            numa linha: quando a faixa quebra, a data sobe junto e encosta no
            último fato. `margin-left: auto` é o que a prega na borda nos dois
            casos.
          */}
          <div className="header-data" hidden={estreita && !dadosAbertos}>
            <div className="label-muted">{dataDaRodada(view.season, view.round)}</div>
            <div className="num" style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>
              {/*
                Na pós-temporada o contador de temporadas já virou, mas o de
                rodadas ainda não. Mostrar "temporada 2, rodada 61 de 61" não
                descreve nada: o que está acontecendo é a pré-temporada.
              */}
              {view.seasonOver
                ? t("hub.preTemporada", { temporada: view.season })
                : t("hub.temporadaRodada", {
                    temporada: view.season,
                    rodada: view.leagueRound?.played ?? view.round,
                    total: view.leagueRound?.total ?? view.totalRounds,
                  })}
            </div>
          </div>
          </div>
        </header>

        {/*
          Barra do hotseat. Só aparece quando há mais de um treinador na mesma
          partida. Trocar de vez não mexe na fila: as ordens de todos viajam
          juntas quando a rodada roda, que é exatamente o que o servidor fará
          com as decisões recebidas no modo online.
        */}
        {view.seats.length > 1 && (
          <div
            className="row"
            style={{
              gap: 8,
              flexWrap: "wrap",
              padding: "8px var(--gutter)",
              borderBottom: "1px solid var(--border)",
              background: "var(--surface)",
            }}
          >
            <span className="label-muted" style={{ whiteSpace: "nowrap" }}>
              {t("hub.vezDe")}
            </span>
            {view.seats.map((assento, indice) => (
              <button
                key={assento.clubId}
                className="ghost"
                disabled={busy || assento.active}
                onClick={() => actions.onSwitchSeat(indice)}
                style={{
                  gap: 8,
                  display: "flex",
                  alignItems: "center",
                  minHeight: 30,
                  padding: "2px 10px",
                  borderColor: assento.active ? "var(--primary)" : undefined,
                  color: assento.active ? "var(--primary)" : undefined,
                  fontWeight: assento.active ? 700 : 400,
                  opacity: 1,
                }}
              >
                <Crest colors={assento.colors} short={assento.clubShort} size={18} />
                <span style={{ fontSize: 12 }}>{assento.managerName}</span>
                {assento.pending > 0 && (
                  <span className="label" style={{ color: "var(--accent)" }}>
                    {assento.pending}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="body">
          <div style={{ minWidth: 0, display: "grid", gap: 14 }}>
            {/*
              Tabela de um lado, ficha do outro, e nada por cima.

              Clicar num jogador abria um diálogo que cobria o elenco inteiro,
              e comparar dois era abrir, fechar e abrir de novo, perdendo a
              linha de onde se veio. Na coluna, a tabela fica onde está e só o
              lado direito troca.

              No celular não há coluna, e ali a ficha continua abrindo por cima:
              é a mesma ficha, só com outra moldura.
            */}
            {section === "elenco" && colunaDoAdm && (
              <div className="elenco-com-ficha adm-com-coluna">
                <SquadPanel
                  view={view}
                  onOpenPlayer={(id) => (ficha?.player.id === id ? ficha.onClose() : actions.onOpenPlayer(id))}
                  selecionado={ficha?.player.id ?? ""}
                />
                {ficha ? (
                  <FichaDoJogador
                    {...ficha}
                    escudo={(tamanho, semSigla) => escudoDoClube(club, cosmeticos.crest, tamanho, semSigla)}
                    sigla={club.short}
                    naColuna
                    estilo={{ position: "sticky", top: 14 }}
                  />
                ) : (
                  <ColunaDoAdm
                    view={view}
                    escudo={(tamanho) => escudoDoClube(club, cosmeticos.crest, tamanho)}
                    aoAbrirClube={actions.onOpenClub}
                  />
                )}
              </div>
            )}
            {section === "elenco" && !colunaDoAdm && <SquadPanel view={view} onOpenPlayer={actions.onOpenPlayer} />}
            {section === "tatica" && (
              <TacticsPanel
                view={view}
                busy={busy}
                onConfirm={actions.onConfirmLineup}
                onAuto={actions.onAutoLineup}
                onOpenClub={actions.onOpenClub}
              />
            )}
            {section === "mercado" &&
              (fichaNaColuna ? (
                <div className="elenco-com-ficha">
                  <MarketPanel
                    view={view}
                    results={market.results}
                    total={market.total}
                    pagina={market.pagina}
                    paginas={market.paginas}
                    busy={busy}
                    onSearch={actions.onSearch}
                    onOpenPlayer={actions.onOpenPlayer}
                    selecionado={ficha?.player.id}
                    aba={abaDoMercado}
                    onAba={setAbaDoMercado}
                    livres={actions.timeB.livres}
                  />
                  {ficha && <FichaDoJogador {...ficha} naColuna />}
                </div>
              ) : (
                <MarketPanel
                  view={view}
                  results={market.results}
                  total={market.total}
                  pagina={market.pagina}
                  paginas={market.paginas}
                  busy={busy}
                  onSearch={actions.onSearch}
                  onOpenPlayer={actions.onOpenPlayer}
                  aba={abaDoMercado}
                  onAba={setAbaDoMercado}
                  livres={actions.timeB.livres}
                />
              ))}
            {section === "competicoes" && (
              <CompetitionsPanel view={view} onOpenPlayer={actions.onOpenPlayer} onOpenClub={actions.onOpenClub} />
            )}
            {section === "clube" && (
              <div className="grid" style={{ gap: 14 }}>
                <div className="row" role="tablist" style={{ gap: 8, flexWrap: "wrap" }}>
                  {(
                    [
                      ["clube", "hub.nav.clube"],
                      ["financas", "hub.nav.financas"],
                      ["carreira", "hub.nav.carreira"],
                    ] as const
                  ).map(([aba, rotulo]) => (
                    <button
                      key={aba}
                      role="tab"
                      aria-selected={abaDoClube === aba}
                      className="ghost"
                      onClick={() => setAbaDoClube(aba)}
                      style={
                        abaDoClube === aba
                          ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                          : undefined
                      }
                    >
                      {t(rotulo)}
                    </button>
                  ))}
                </div>
                {abaDoClube === "clube" && (
                  <ClubPanel view={view} busy={busy} onClubAction={actions.onClubAction} />
                )}
                {abaDoClube === "financas" && <FinancePanel view={view} />}
                {abaDoClube === "carreira" && (
                  <CareerPanel
                    view={view}
                    onExportPack={actions.onExportPack}
                    onSaveNow={online ? null : actions.onSaveNow}
                    busy={busy}
                  />
                )}
              </div>
            )}
            {section === "calendario" && <Calendario view={view} aoAbrir={setConfronto} />}
            {section === "base" && (
              <TimeBPanel
                view={view}
                busy={busy}
                acoes={actions.timeB}
                onIrAoElenco={() => setSection("elenco")}
              />
            )}
            {section === "relatorios" && (
              <RelatoriosPanel view={view} ultimoJogo={ultimoJogo} onOpenPlayer={actions.onOpenPlayer} />
            )}
            {section === "noticias" && (
              <Jornal view={view} onOpenPlayer={actions.onOpenPlayer} onResponderColetiva={actions.onResponderColetiva} />
            )}
            {section === "opcoes" && <Opcoes aoSairDaCarreira={() => setSaindo(true)} />}

            {/*
              As telas de conta rodam aqui dentro, sem voltar próprio.

              Elas já aceitavam `aoVoltar` opcional, e sem ele não desenham o
              botão: quem volta é a barra de navegação, que continua na tela.
            */}
            {online && section === "ligas" && <Ligas aoAbrirLiga={online.aoAbrirLiga} />}
            {online && section === "ranqueado" && (
              <Ranqueado
                aoAbrirLiga={online.aoAbrirLiga}
                aoAbrirLoja={() => setSection("loja")}
              />
            )}
            {online && section === "loja" && <Loja />}
            {online && section === "conta" && <Conta />}

          </div>
        </div>

        <div className="actionbar">
          {/*
            A partida fica aqui, e em nenhum outro lugar.

            É o dado que se olha imediatamente antes de apertar o botão que joga
            a rodada, então ele mora ao lado do botão. Saiu do cabeçalho junto:
            nos dois lugares seria a mesma informação duas vezes na mesma tela.

            Sem jogo marcado não há o que mostrar, e aí a faixa volta a ser o
            recado da rodada.
          */}
          {view.nextMatch && !view.seasonOver && !colunaDoAdm ? (
            <ProximaPartidaNoRodape view={view} cosmetico={cosmeticos.crest} aoAbrirClube={actions.onOpenClub} />
          ) : (
            <span className="row" style={{ gap: 12, minWidth: 0 }}>
              <span style={{ fontSize: 12, color: note ? "var(--accent)" : "var(--text-muted)" }}>
                {note
                  ? textoDaSessao(note.key, note.params)
                  : view.seasonOver
                    ? t("hub.temporadaEncerrada")
                    : view.nextMatch
                      ? ""
                      : t("hub.semJogoMarcado")}
              </span>
            </span>
          )}

          {/*
            Um botão só: jogar a rodada é assisti-la.

            Eram dois, "Assistir" e "Jogar rodada", e a diferença entre eles era
            ver ou não ver o que ia acontecer de qualquer jeito. Quem não quer
            ver tem, dentro da partida, o "pular para o resultado", que chega no
            mesmo lugar em um toque.

            Em rodada de folga e na virada de temporada não há partida, e o
            mesmo caminho fecha a rodada direto: quem decide é o motor, ao abrir
            a rodada e não achar jogo do clube.
          */}
          <div className="rodape-acoes">
            <SinoDeNotificacoes view={view} onAbrir={actions.onLerNotificacoes} />
            {/* As decisões na fila, com o número no ícone: é o que vai junto quando a rodada rodar. */}
            <FilaDaRodada view={view} busy={busy} onCancel={actions.onCancelDecision} />
            {/* As propostas recebidas, com o número das que ainda esperam resposta. */}
            <PropostasRecebidas view={view} busy={busy} onRespond={actions.onRespondOffer} />
            <button className="primary" onClick={actions.onWatch} disabled={busy}>
              {busy ? t("hub.simulando") : advanceLabel}
            </button>
          </div>
        </div>
      </div>

      {/*
        O escudo entra aqui e não em quem montou a ficha: o cosmético da conta
        mora neste arquivo, e mandá-lo para cima só para descer de volta pediria
        a leitura do servidor em mais um lugar.
      */}
      {ficha && !fichaNaColuna && (
        <PlayerSheet
          {...ficha}
          escudo={(tamanho, semSigla) => escudoDoClube(club, cosmeticos.crest, tamanho, semSigla)}
          sigla={club.short}
        />
      )}

      {confronto && section === "calendario" && (
        <DialogoDoConfronto
          view={view}
          jogo={confronto}
          aoFechar={() => setConfronto(null)}
          aoAbrirClube={actions.onOpenClub}
        />
      )}
      {ajuda && <AjudaDeAtalhos aoFechar={() => setAjuda(false)} />}
      {saindo && <DialogoDeSaida saida={actions.saida} aoFechar={() => setSaindo(false)} />}
    </div>
  );
}

/** A reputação do técnico (0 a 100) em palavra, pelas mesmas faixas das estrelas. */
function rotuloDaReputacao(reputacao: number) {
  if (reputacao >= 85) return "hub.reputacaoTecnico.elite" as const;
  if (reputacao >= 70) return "hub.reputacaoTecnico.alta" as const;
  if (reputacao >= 50) return "hub.reputacaoTecnico.boa" as const;
  if (reputacao >= 30) return "hub.reputacaoTecnico.regular" as const;
  return "hub.reputacaoTecnico.baixa" as const;
}

/*
 * A sanfona do cabeçalho lembra da escolha.
 *
 * Quem recolheu os dados recolheu porque quer a tela para o elenco, e reabrir
 * sozinho a cada partida seria desfazer a escolha toda vez. Em try/catch como
 * as outras preferências: janela anônima e armazenamento desligado jogam nas
 * duas pontas, e sem isto a escolha vale até fechar a aba.
 */
const CHAVE_DOS_DADOS = "lm:cabecalho";

function dadosGuardados(): boolean {
  try {
    return localStorage.getItem(CHAVE_DOS_DADOS) !== "fechado";
  } catch {
    return true;
  }
}

/**
 * O botão de jogar: a rodada da liga ou do estadual, que é a que quem joga
 * conta, ou a fase da copa. A rodada do calendário do mundo abre com os
 * estaduais, e um clube sem estadual via "Jogar rodada 10" na estreia.
 */
function rotuloDoJogo(t: Traduzir, jogo: NonNullable<ClubView["nextMatch"]>): string {
  const fase = rotuloDeFase(jogo.stage);
  if (jogo.matchday !== null || !fase) return t("hub.jogarRodada", { rodada: jogo.matchday ?? jogo.round });
  return t("hub.jogarFase", { fase });
}

function guardarDados(aberto: boolean): void {
  try {
    localStorage.setItem(CHAVE_DOS_DADOS, aberto ? "aberto" : "fechado");
  } catch {
    // Sem lugar para guardar. A escolha vale até fechar a aba, e pronto.
  }
}

/**
 * O escudo do clube do jogador, entre os três possíveis.
 *
 * O desenhado pela pessoa vem primeiro: ela fez aquilo e o clube é dela. O
 * cosmético comprado vem depois, e vale para clube que ela não desenhou. O de
 * duas cores é o de sempre, para os 320 gerados.
 *
 * Virou função quando o rodapé do celular passou a mostrar o escudo também: a
 * ordem é regra e não pode existir em duas cópias que um dia discordam.
 */
function escudoDoClube(
  club: ClubView["club"],
  cosmetico: CrestParams | null,
  size: number,
  semSigla = false,
): ReactNode {
  if (club.crest) {
    return <EscudoDoClube crest={club.crest} short={club.short} size={size} semSigla={semSigla} />;
  }
  if (cosmetico) {
    return (
      <EscudoCosmetico params={cosmetico} short={club.short} size={size} semSigla={semSigla} />
    );
  }
  return <Crest colors={club.colors} short={club.short} size={size} semSigla={semSigla} />;
}

/**
 * A próxima partida na barra de ações.
 *
 * Ali ficavam "Tudo pronto. Boa sorte." e "Última rodada processada em 127 ms".
 * Nenhuma das duas diz nada sobre o jogo, e as duas ocupavam a faixa que fica
 * fixa no pé da tela. No lugar entra o que a pessoa quer saber antes de apertar
 * jogar: contra quem, onde e por qual competição.
 *
 * Em linha no computador e empilhada no celular, pelo espaço que cada um tem: a
 * barra larga cabe tudo numa linha, e a estreita precisa dos escudos em cima
 * dos nomes para os dois times caberem lado a lado.
 *
 * Os nomes quebram em quantas linhas precisarem, com `anywhere`: clube de nome
 * longo numa tela de 360 nem sempre tem espaço onde quebrar, e cortar o nome
 * deixa dois adversários de começo parecido indistinguíveis.
 */
function ProximaPartidaNoRodape({
  view,
  cosmetico,
  aoAbrirClube,
}: {
  view: ClubView;
  cosmetico: CrestParams | null;
  aoAbrirClube: (clubId: string) => void;
}): ReactNode {
  const t = useT();
  const next = view.nextMatch;
  if (!next) return null;

  return (
    <div className="rodape-partida">
      <div className="rodape-times">
        <span className="rodape-time">
          {escudoDoClube(view.club, cosmetico, 26)}
          <span className="rodape-nome">{view.club.name}</span>
        </span>

        <span className="rodape-vs label-muted">{t("hub.versus")}</span>

        <BotaoDoClube clubId={next.opponentId} nome={next.opponent} aoAbrir={aoAbrirClube} className="rodape-time">
          <Crest colors={next.opponentColors} short={next.opponentShort} size={26} />
          <span className="rodape-nome">{next.opponent}</span>
        </BotaoDoClube>
      </div>

      <span className="rodape-campeonato">
        {next.competition}
        {next.stage === "liga" || next.stage === "estadual"
          ? t("hub.competicaoRodada", { rodada: next.matchday ?? next.round })
          : t("hub.competicaoFase", { fase: rotuloDeFase(next.stage) })}
        {" · "}
        {next.home ? t("hub.emCasa") : t("hub.foraDeCasa")}
        {next.derby && <b className="rodape-classico">{t("hub.classico")}</b>}
      </span>
    </div>
  );
}

/**
 * Um rótulo e o que ele vale.
 *
 * Aceita texto ou desenho: a moral tem uma carinha e os últimos jogos são cinco
 * fichas, e nenhum dos dois vira string. Quem passa `children` manda no lugar
 * de `value`.
 */
function Fact({
  label,
  value,
  children,
  quebra,
}: {
  label: string;
  value?: string;
  children?: ReactNode;
  /**
   * Deixa o valor quebrar em mais de uma linha, num teto de 140.
   *
   * O padrão é não quebrar, porque quase todo fato aqui é número ou nome curto
   * e uma quebra no meio deles atrapalha a leitura de relance. O objetivo é a
   * exceção: é frase, e sem isto "Terminar no meio de tabela" esticava 168
   * pixels numa fileira que já estava no limite, empurrando o grupo inteiro
   * para uma segunda linha. Frase que quebra precisa de onde quebrar.
   */
  quebra?: boolean;
}): ReactNode {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="label-muted">{label}</div>
      <div className={quebra ? "fato-frase" : "fato-valor"}>{children ?? value}</div>
    </div>
  );
}
